import React, { useState } from 'react'
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import GenericButton from '../../../../components/GenericButton'
import { ButtonType } from '../../../../components/GenericButton/styles'
import AnimatedContainer from '../../../../components/Animations/AnimatedContainer'

export interface ApplyDefaultQuestionsConflict {
  sectionId: string
  sectionTitle: string
  instructorUserId: string
  instructorName: string
  existingQuestionCount: number
  // Backend CR-270 follow-up: classification is now all-or-none per section — a section with
  // any instructor-touched item is left completely untouched; only a fully pristine section is
  // fully refreshed. isFullyPristine gates which of those two outcomes this row will get.
  isFullyPristine: boolean
  preservedQuestionCount: number
  replacedDefaultCount: number
  seededQuestionCount: number
  // Preview of what `force: true` would additionally do to this row, shown even though force
  // isn't the default path, so the Advanced section can preview the destructive option.
  forceReplacedCount: number
  forceSeededCount: number
}

export interface ApplyDefaultQuestionsConflictSummary {
  totalConflicts: number
  willBeKeptAsIsCount: number
  willBeRefreshedCount: number
  forceWouldReplaceCount: number
  forceWouldReplaceQuestionCount: number
}

interface ApplyDefaultQuestionsConflictModalProps {
  conflicts: ApplyDefaultQuestionsConflict[]
  summary: ApplyDefaultQuestionsConflictSummary
  onCancel: () => void
  onConfirm: () => void
  onForceConfirm: () => void
  isSubmitting?: boolean
}

const instructorNoun = (count: number): string => (count === 1 ? 'instructor' : 'instructors')

export const ApplyDefaultQuestionsConflictModal: React.FC<ApplyDefaultQuestionsConflictModalProps> = ({
  conflicts,
  summary,
  onCancel,
  onConfirm,
  onForceConfirm,
  isSubmitting = false
}) => {
  const [showDetails, setShowDetails] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  return (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography fontWeight="bold" fontSize="0.875rem">
          {summary.totalConflicts} {instructorNoun(summary.totalConflicts)} already{' '}
          {summary.totalConflicts === 1 ? 'has' : 'have'} questions — {summary.willBeKeptAsIsCount}{' '}
          {summary.willBeKeptAsIsCount === 1 ? 'has' : 'have'} edits and will be kept as-is,{' '}
          {summary.willBeRefreshedCount} {summary.willBeRefreshedCount === 1 ? 'is' : 'are'} untouched
          and will be refreshed.
        </Typography>
      </Alert>

      <GenericButton
        label={showDetails ? 'Hide details' : 'Show details'}
        buttonType={ButtonType.Ghost}
        variant="text"
        onClick={() => { setShowDetails((prev) => !prev) }}
        sx={{ textDecoration: 'underline', mb: 2 }}
      />

      <AnimatedContainer isVisible={showDetails}>
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Section</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Instructor</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Existing Questions</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conflicts.map((conflict) => (
                <TableRow key={`${conflict.sectionId}-${conflict.instructorUserId}`}>
                  <TableCell>{conflict.sectionTitle}</TableCell>
                  <TableCell>{conflict.instructorName}</TableCell>
                  <TableCell>{conflict.existingQuestionCount}</TableCell>
                  <TableCell>{conflict.isFullyPristine ? 'Will be refreshed' : 'Kept as-is'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AnimatedContainer>

      <GenericButton
        label={showAdvanced ? 'Hide advanced options' : 'Advanced options'}
        buttonType={ButtonType.Ghost}
        variant="text"
        onClick={() => { setShowAdvanced((prev) => !prev) }}
        sx={{ textDecoration: 'underline', mb: 2 }}
      />

      <AnimatedContainer isVisible={showAdvanced}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography fontWeight="bold" fontSize="0.875rem">
            This will replace {summary.forceWouldReplaceQuestionCount} questions across{' '}
            {summary.forceWouldReplaceCount} {instructorNoun(summary.forceWouldReplaceCount)}, including
            questions they have written themselves.
          </Typography>
        </Alert>
        <GenericButton
          label="Force Replace All"
          buttonType={ButtonType.Destructive}
          disabled={isSubmitting}
          onClick={onForceConfirm}
          sx={{ mb: 3 }}
        />
      </AnimatedContainer>

      <DefaultModalActions
        destructiveButtonProps={{ onClick: onCancel, disabled: isSubmitting }}
        confirmButtonProps={{
          onClick: onConfirm,
          text: 'Apply Template',
          buttonType: ButtonType.Primary,
          loading: isSubmitting
        }}
      />
    </>
  )
}
